import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NgZone,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
    input,
    model,
    signal,
} from '@angular/core';
import { find } from 'lodash-es';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import { PhotoContestApiService } from 'apps/account/src/account-shared/services/photo-contest-api.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { LegacyShimsModule } from '../../../../account-shared/legacy-shims/legacy-shims.module';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtEnsureModalSingleScrollDirective } from '../../stories/story-modal/ensure-modal-single-scroll.directive';
import { TranslateModule } from '@ngx-translate/core';
import { CapitalizePipe } from './contest-sheets.pipes';
import { FtContestSubmissionsComponent } from './contest-submissions.component';
import { FtContestWinnersComponent } from './contest-winners.component';
import { FtContestDetailsComponent } from './contest-details.component';
import { FtContestInvitationsComponent } from './contest-invitations.component';

interface ContestTab {
    state: string;
    name: string;
    title: string;
    isActive: boolean;
}

/**
 * Native contest-editor modal chrome: the modal shell, header (contest name +
 * activate toggle), and the `<ft-sections>` tab nav that switches between the
 * submissions/winners/details/invitations sheets. Loads and activate-toggles the
 * contest via PhotoContestApiService. Routed component-state input bindings.
 */
@Component({
    selector: 'ft-contest-container-ng',
    imports: [
        LegacyShimsModule,
        FtModalComponent,
        FtEnsureModalSingleScrollDirective,
        TranslateModule,
        CapitalizePipe,
        FtContestSubmissionsComponent,
        FtContestWinnersComponent,
        FtContestDetailsComponent,
        FtContestInvitationsComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './contest-container.component.html',
    styleUrls: ['./contest-container.component.scss'],
})
export class FtContestContainerComponent implements OnInit, OnDestroy {
    readonly propertyUuid = input.required<string>();
    readonly contestUuid = input.required<string>();
    readonly activeSection = model('winners');
    /** The state `close` resolve — `() => $state.go('^')`. */
    readonly close = input<() => void>();

    protected readonly contestsTabs = signal<ContestTab[]>([
        { state: '^.submissions', name: 'submissions', title: 'Submissions', isActive: false },
        { state: '^.winners', name: 'winners', title: 'Winners', isActive: false },
        { state: '^.details', name: 'details', title: 'Details', isActive: false },
        { state: '^.invitations', name: 'invitations', title: 'Invitations', isActive: false },
    ]);

    /** Mirrors the v1 `$ftCHC` header model (populated after PhotoContest.load). */
    protected readonly chc = signal<{ isActive: boolean; isEnabled: boolean; language: any }>({
        isActive: false,
        isEnabled: false,
        language: {},
    });

    private readonly zone = inject(NgZone);
    private readonly photoContest: any = inject(PhotoContestApiService);
    // WS5 #10 inc 2i: 'flipto.photo-contest-changed' is native↔native (emitters =
    // the native contest-details/contest-invitations sheets) → the native event bus.
    private readonly eventBus: EventBusService = inject(EventBusService);
    private readonly parse: any = inject(ParseService);
    private deregChanged?: () => void;

    // WS5 #10 inc 2q: the v1 `emailBannerUpdates` SignalR start/stop the adapter
    // carried was INERT here — its broadcast's only listener (v1 ftEmailImagesPreview)
    // never renders in this container and self-connects in its own styling context —
    // so the grab is deleted rather than ported (deliberate, provably-safe divergence).

    ngOnInit(): void {
        this.contestsTabs().forEach((tab) => {
            tab.isActive = tab.name === this.activeSection();
        });

        // Port of the inline $ftContestHeaderController (contestContainer.component.ts:67-113).
        Promise.resolve(
            this.photoContest.load({ contestId: this.contestUuid(), propertyUuid: this.propertyUuid() }),
        ).then((response: any) => {
            this.zone.run(() => {
                this.chc.set({
                    isActive: response.isActive == 1,
                    language: find(response.languages.item, { code: 'en' }),
                    isEnabled: this.getIsEnabled(response),
                });
            });
        });

        // v1 `$scope.$on('flipto.photo-contest-changed', ...)` — now on the native
        // event bus (inc 2i; all emitters are native); deregister on destroy.
        this.deregChanged = this.eventBus.on('flipto.photo-contest-changed', (contest: any) => {
            const englishContest = find(contest.languages.item, { code: 'en' });
            this.chc().language.contestName = englishContest.contestName;
            this.chc().isEnabled = this.getIsEnabled(contest);
        });
    }

    ngOnDestroy(): void {
        this.deregChanged?.();
    }

    protected sectionChanged(section: ContestTab): void {
        this.activeSection.set(section.name);
        this.contestsTabs().forEach((tab) => {
            tab.isActive = tab.name === section.name;
        });
    }

    /** v1 `injectables.onReviewFinalists` — switch to the Winners tab. */
    protected onReviewFinalists(): void {
        this.sectionChanged(this.contestsTabs()[1]);
    }

    protected closeContainer(): void {
        this.close()?.();
    }

    /** v1 `$ftCHC.updateState` — activate/deactivate toggle with the disable warning. */
    protected updateState(): void {
        if (
            !this.chc().isActive &&
            !window.confirm(
                'Warning! Disabling this contest will: \n  • Remove these photo submissions from Story Explorer and Story River.\n  • Disable sharing and voting by storytellers who joined the campaign.\n  • Prevent these stories from being used in other areas of your platform.',
            )
        ) {
            this.chc().isActive = true;
            return;
        }
        // The contests and audiences lists listen for this to show the new status.
        Promise.resolve(this.photoContest.activate(this.propertyUuid(), this.contestUuid(), this.chc().isActive)).then(() =>
            this.zone.run(() => this.eventBus.emit('flipto.photo-contest-activated', this.contestUuid())),
        );
    }

    /** Verbatim port of the v1 header controller's getIsEnabled(response). */
    private getIsEnabled(response: any): boolean {
        const languages = this.parse.arrayOrDefault(response.languages);
        const langEn = find(languages, (lang: any) => lang.code === 'en');
        if (!langEn.contestName) return false;
        if (!langEn.followerMessage) return false;
        if (!langEn.guestMessage) return false;
        if (!langEn.releaseTerms) return false;
        if (!langEn.terms) return false;

        if (!response.startDate) return false;
        if (!response.endDate) return false;

        return !!response.finalistsIncentives && !!response.finalistsIncentives.firstWinnerIncentive;
    }
}
